/**
 * QuickFilters Component
 *
 * Preset filter buttons for common transaction views.
 * Shows badge counts for filters like "Needs Attention".
 *
 * @see types/accounting.ts for TransactionFilters and TransactionStats types
 */

import { useMemo, useCallback } from 'react';
import clsx from 'clsx';
import {
  startOfMonth,
  endOfMonth,
  subMonths,
  format,
} from 'date-fns';
import type {
  TransactionFilters,
  TransactionStats,
  ClassificationStatus,
} from '@/types/accounting';

export interface QuickFiltersProps {
  /** Current active filters */
  currentFilters: TransactionFilters;
  /** Transaction statistics for badge counts */
  stats: TransactionStats;
  /** Callback when a quick filter is selected */
  onSelect: (filters: Partial<TransactionFilters>) => void;
}

interface QuickFilter {
  id: string;
  label: string;
  badge?: (stats: TransactionStats) => number;
  getFilters: () => Partial<TransactionFilters>;
  isActive: (currentFilters: TransactionFilters) => boolean;
}

/**
 * Format a date for filter values (YYYY-MM-DD)
 */
function formatDateValue(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/**
 * Check if arrays have the same elements (order-independent)
 */
function arraysEqual<T>(a: T[] | undefined | null, b: T[]): boolean {
  if (!a || a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
}

/**
 * Define quick filter presets
 */
function getQuickFilters(): QuickFilter[] {
  const now = new Date();
  const thisMonthStart = startOfMonth(now);
  const thisMonthEnd = endOfMonth(now);
  const lastMonthStart = startOfMonth(subMonths(now, 1));
  const lastMonthEnd = endOfMonth(subMonths(now, 1));

  const needsAttentionStatuses: ClassificationStatus[] = ['pending', 'needs_review'];

  return [
    {
      id: 'needs-attention',
      label: 'Needs Attention',
      badge: (stats) => stats.pendingCount + stats.needsReviewCount,
      getFilters: () => ({
        classificationStatus: needsAttentionStatuses,
        dateFrom: null,
        dateTo: null,
        owner: null,
        accountId: null,
        search: null,
      }),
      isActive: (filters) =>
        arraysEqual(filters.classificationStatus, needsAttentionStatuses) &&
        !filters.dateFrom &&
        !filters.dateTo &&
        !filters.owner &&
        !filters.accountId &&
        !filters.search,
    },
    {
      id: 'this-month',
      label: 'This Month',
      getFilters: () => ({
        dateFrom: formatDateValue(thisMonthStart),
        dateTo: formatDateValue(thisMonthEnd),
        classificationStatus: [],
        owner: null,
        accountId: null,
        search: null,
      }),
      isActive: (filters) =>
        filters.dateFrom === formatDateValue(thisMonthStart) &&
        filters.dateTo === formatDateValue(thisMonthEnd) &&
        (!filters.classificationStatus || filters.classificationStatus.length === 0) &&
        !filters.owner &&
        !filters.accountId &&
        !filters.search,
    },
    {
      id: 'last-month',
      label: 'Last Month',
      getFilters: () => ({
        dateFrom: formatDateValue(lastMonthStart),
        dateTo: formatDateValue(lastMonthEnd),
        classificationStatus: [],
        owner: null,
        accountId: null,
        search: null,
      }),
      isActive: (filters) =>
        filters.dateFrom === formatDateValue(lastMonthStart) &&
        filters.dateTo === formatDateValue(lastMonthEnd) &&
        (!filters.classificationStatus || filters.classificationStatus.length === 0) &&
        !filters.owner &&
        !filters.accountId &&
        !filters.search,
    },
    {
      id: 'estate',
      label: 'Estate Only',
      getFilters: () => ({
        owner: 'estate',
        dateFrom: null,
        dateTo: null,
        classificationStatus: [],
        accountId: null,
        search: null,
      }),
      isActive: (filters) =>
        filters.owner === 'estate' &&
        !filters.dateFrom &&
        !filters.dateTo &&
        (!filters.classificationStatus || filters.classificationStatus.length === 0) &&
        !filters.accountId &&
        !filters.search,
    },
    {
      id: 'family',
      label: 'Family Only',
      getFilters: () => ({
        owner: 'family',
        dateFrom: null,
        dateTo: null,
        classificationStatus: [],
        accountId: null,
        search: null,
      }),
      isActive: (filters) =>
        filters.owner === 'family' &&
        !filters.dateFrom &&
        !filters.dateTo &&
        (!filters.classificationStatus || filters.classificationStatus.length === 0) &&
        !filters.accountId &&
        !filters.search,
    },
  ];
}

/**
 * QuickFilters renders a row of preset filter buttons.
 *
 * Features:
 * - Badge counts for "Needs Attention" filter
 * - Visual highlight for active filter
 * - One-click filter application
 *
 * @example
 * ```tsx
 * <QuickFilters
 *   currentFilters={filters}
 *   stats={stats}
 *   onSelect={(newFilters) => handleFilterChange(newFilters)}
 * />
 * ```
 */
export function QuickFilters({
  currentFilters,
  stats,
  onSelect,
}: QuickFiltersProps) {
  const quickFilters = useMemo(() => getQuickFilters(), []);

  const handleFilterClick = useCallback(
    (filter: QuickFilter) => {
      onSelect(filter.getFilters());
    },
    [onSelect]
  );

  return (
    <div
      className="flex flex-wrap gap-2"
      role="group"
      aria-label="Quick filters"
      data-testid="quick-filters"
    >
      {quickFilters.map((filter) => {
        const isActive = filter.isActive(currentFilters);
        const badgeCount = filter.badge?.(stats);
        const showBadge = badgeCount !== undefined && badgeCount > 0;

        return (
          <button
            key={filter.id}
            type="button"
            onClick={() => handleFilterClick(filter)}
            className={clsx(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full',
              'transition-colors duration-150',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
              isActive
                ? 'bg-blue-100 text-blue-800 border border-blue-200'
                : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
            )}
            aria-pressed={isActive}
            data-testid={`quick-filter-${filter.id}`}
          >
            {filter.label}
            {showBadge && (
              <span
                className={clsx(
                  'inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold rounded-full min-w-[1.25rem]',
                  isActive
                    ? 'bg-blue-200 text-blue-800'
                    : 'bg-amber-100 text-amber-700'
                )}
                aria-label={`${badgeCount} items`}
              >
                {badgeCount}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default QuickFilters;
